import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type PropsWithChildren,
    type ReactNode
} from 'react';
import { CookieConsentBanner } from '../components/cookie-consent-banner';
import {
    getCookieConsent,
    hasCookieConsentDecision,
    isThirdPartyConsentGranted,
    resetCookieConsent,
    setCookieConsentAccepted,
    setCookieConsentDeclined,
    subscribeCookieConsentChanged
} from '../util/cookie-consent';
import { bootGtmIfConfigured } from '../util/boot-gtm';

type CookieConsentContextValue = {
    consent: ReturnType<typeof getCookieConsent>;
    thirdPartyAllowed: boolean;
    acceptCookies: () => void;
    declineCookies: () => void;
    openCookieSettings: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: PropsWithChildren<{ children?: ReactNode }>) {
    const [bannerVisible, setBannerVisible] = useState(() => !hasCookieConsentDecision());
    const [consent, setConsent] = useState(getCookieConsent);

    const syncConsent = useCallback(() => {
        setConsent(getCookieConsent());
        setBannerVisible(!hasCookieConsentDecision());
    }, []);

    useEffect(() => {
        if (isThirdPartyConsentGranted()) {
            void bootGtmIfConfigured();
        }
        return subscribeCookieConsentChanged(syncConsent);
    }, [syncConsent]);

    const acceptCookies = useCallback(() => {
        setCookieConsentAccepted();
        setConsent('accepted');
        setBannerVisible(false);
        void bootGtmIfConfigured();
    }, []);

    const declineCookies = useCallback(() => {
        setCookieConsentDeclined();
        setConsent('declined');
        setBannerVisible(false);
    }, []);

    const openCookieSettings = useCallback(() => {
        resetCookieConsent();
        setConsent(null);
        setBannerVisible(true);
    }, []);

    const value = useMemo<CookieConsentContextValue>(
        () => ({
            consent,
            thirdPartyAllowed: consent === 'accepted',
            acceptCookies,
            declineCookies,
            openCookieSettings
        }),
        [acceptCookies, consent, declineCookies, openCookieSettings]
    );

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
            {bannerVisible ? (
                <CookieConsentBanner
                    onAccept={acceptCookies}
                    onDecline={declineCookies}
                />
            ) : null}
        </CookieConsentContext.Provider>
    );
}

export function useCookieConsent(): CookieConsentContextValue {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error('useCookieConsent must be used within CookieConsentProvider');
    }
    return context;
}
